// src/components/QuizResults.js — calm post-quiz review with design-system primitives
import React, { useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import {
  CheckCircle, XCircle, RotateCcw, Home, Flag, ChevronDown, ChevronRight
} from 'lucide-react';
import { apiService } from '../services/api';
import { formatTime } from '../utils/helpers';
import MarkdownRenderer from './common/MarkdownRenderer';
import { Button, Card, Breadcrumbs } from './ui';
import { useTutor } from '../contexts/TutorRuntimeContext';

// Results voice — forward-framed, never shaming (no-ambush is structural).
// `mark` is the phrase that gets the highlighter treatment in the title.
function performanceFor(successRate) {
  const pct = successRate * 100;
  if (pct >= 80) return { title: 'You own', mark: 'this ground', sub: 'Strong across the board — ready to push into the next domain.' };
  if (pct >= 60) return { title: 'You found', mark: 'the edge', sub: 'Most of it is solid; the misses share a thread worth pulling.' };
  if (pct >= 40) return { title: 'The shape is', mark: 'forming', sub: 'A few concepts are ready to click — drill them while they\'re warm.' };
  return                 { title: 'Now we know', mark: 'where to aim', sub: 'This quiz mapped the terrain. The next session starts exactly there.' };
}

export default function QuizResults() {
  const { courseId, domain } = useParams();
  const location = useLocation();
  const { quizResults, quiz, answers, timesTaken } = location.state || {};
  // Course name is only used in the breadcrumb. Best-effort fetch — no
  // spinner, no error: if it fails, the breadcrumb just hides that
  // segment and the page still renders.
  const { sendToTutor } = useTutor();
  const [courseName, setCourseName] = useState(null);
  useEffect(() => {
    let alive = true;
    apiService.getCourse(courseId)
      .then((res) => { if (alive) setCourseName(res.data?.name); })
      .catch(() => {});
    return () => { alive = false; };
  }, [courseId]);

  if (!quizResults || !quiz) {
    return (
      <div className="min-h-screen bg-surface-muted flex items-center justify-center px-6">
        <div className="text-center space-y-4 max-w-md">
          <p className="text-read">Quiz results not found.</p>
          <Button to={`/course/${courseId}`} variant="primary">
            <Home size={14} /> Back to course
          </Button>
        </div>
      </div>
    );
  }

  const { summary, question_evaluations } = quizResults;
  const perf = performanceFor(summary.success_rate);
  const domainLabel = domain.replaceAll('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  const nextConcept = quizResults.next_focus_areas?.[0] || null;

  return (
    <div className="min-h-screen bg-surface-muted">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-5">

        <Breadcrumbs items={[
          { label: 'My Courses', to: '/my-courses' },
          { label: courseName, to: `/course/${courseId}` },
          { label: domainLabel, to: `/course/${courseId}/quiz/${domain}` },
          { label: 'Quiz complete' },
        ]} />

        <div className="results-head">
          <div className="rh-eyebrow">quiz complete · {domainLabel}</div>
          <div className="rh-title">{perf.title} <span className="hl">{perf.mark}</span>.</div>
          <div className="rh-sub">{perf.sub}</div>
        </div>

        <div className="score-ring">
          <div className="sr-num">{summary.overall_score.toFixed(1)}<small>/10</small></div>
          <div className="sr-meta">
            {summary.total_questions} questions<br />
            <b>{summary.correct_answers} correct</b> · {formatTime(summary.total_time_seconds)} total
          </div>
        </div>

        <div className="results-actions">
          <Button to={`/course/${courseId}/quiz/${domain}`} variant="primary" size="sm">
            <RotateCcw size={14} /> Take another
          </Button>
          <Button to={`/course/${courseId}`} variant="secondary" size="sm">
            <Home size={14} /> Dashboard
          </Button>
        </div>

        {nextConcept && (
          <div className="results-cta">
            <div className="rc-body">
              <div className="rc-eyebrow">next thread to pull</div>
              <div className="rc-title">Want to clear <span className="hl">{nextConcept}</span> for good?</div>
              <div className="rc-sub">The tutor can drill exactly this edge, grounded in what you just answered.</div>
            </div>
            <button
              type="button"
              className="fn-btn fn-btn--primary"
              onClick={() => sendToTutor(`Help me with ${nextConcept} in ${courseId} — I want to clear it after this quiz.`)}
            >
              Ask the tutor
            </button>
          </div>
        )}

        {/* Insights: study recs + next focus */}
        {(quizResults.study_recommendations?.length > 0 || quizResults.next_focus_areas?.length > 0) && (
          <div className="grid sm:grid-cols-2 gap-6">
            {quizResults.study_recommendations?.length > 0 && (
              <div className="space-y-2">
                <h2 className="text-xs uppercase tracking-wide text-faint font-normal">
                  Study recommendations
                </h2>
                <ul className="space-y-1.5">
                  {quizResults.study_recommendations.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-read">
                      <span className="w-1 h-1 rounded-full bg-faint mt-2 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {quizResults.next_focus_areas?.length > 0 && (
              <div className="space-y-2">
                <h2 className="text-xs uppercase tracking-wide text-faint font-normal">
                  Next focus areas
                </h2>
                <ul className="space-y-1.5">
                  {quizResults.next_focus_areas.map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-read">
                      <span className="w-1 h-1 rounded-full bg-faint mt-2 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Question review */}
        <div className="space-y-3">
          <h2 className="text-xs uppercase tracking-wide text-faint font-normal">
            Question review
          </h2>
          <div className="space-y-2">
            {question_evaluations.map((evaluation, idx) => {
              const question = quiz.questions[idx];
              const userAnswer = answers[idx] || [];
              const timeTaken = timesTaken[idx] || 0;
              return (
                <QuestionRow
                  key={idx}
                  idx={idx}
                  question={question}
                  evaluation={evaluation}
                  userAnswer={userAnswer}
                  timeTaken={timeTaken}
                />
              );
            })}
          </div>
        </div>

      </div>
    </div>
  );
}

// --- helpers -----------------------------------------------------------------

// Per-question result, collapsed by default. The header row carries
// everything you need for triage (right/wrong, grade, time, your pick
// vs correct) so you can scan the list. Click to expand for stem,
// explanation, concepts-to-review.
function QuestionRow({ idx, question, evaluation, userAnswer, timeTaken }) {
  const [open, setOpen] = React.useState(false);
  const isCorrect = evaluation.is_correct;
  const correctIds = question.correct_option_ids || [];
  const yourPickStr = userAnswer.length ? userAnswer.join(', ') : '—';
  const correctStr = correctIds.length ? correctIds.join(', ') : '—';
  return (
    <Card className="overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-surface-subtle transition-colors"
      >
        <span className="flex-shrink-0">
          {open
            ? <ChevronDown size={14} className="text-faint" />
            : <ChevronRight size={14} className="text-faint" />}
        </span>
        <span className="flex-shrink-0">
          {isCorrect
            ? <CheckCircle size={16} className="text-status-good" />
            : <XCircle size={16} className="text-status-bad" />}
        </span>
        <span className="text-sm font-medium text-ink flex-shrink-0">
          Q{idx + 1}
        </span>
        <span className="text-xs text-mutedink flex-1 truncate min-w-0">
          You: <span className={isCorrect ? 'text-status-good font-medium' : 'text-status-bad font-medium'}>{yourPickStr}</span>
          {!isCorrect && (
            <>
              <span className="text-faint mx-1.5">·</span>
              Correct: <span className="text-status-good font-medium">{correctStr}</span>
            </>
          )}
        </span>
        <span className="flex items-center gap-3 text-xs text-mutedink flex-shrink-0">
          <span className="tabular-nums">{evaluation.grade}/10</span>
          <span className="text-faint">·</span>
          <span className="tabular-nums">{formatTime(timeTaken)}</span>
        </span>
      </button>

      {open && (
        <div className="px-4 pb-4 pt-1 border-t border-line">
          <div className="mt-3 mb-3">
            <MarkdownRenderer
              content={question.question_text}
              className="prose prose-sm max-w-none text-ink"
            />
          </div>

          {question.options?.length > 0 ? (
            <OptionsReview options={question.options} picked={userAnswer} correctIds={correctIds} />
          ) : (
            <div className="grid sm:grid-cols-2 gap-3 mb-4">
              <AnswerBlock label="Your answer" values={userAnswer} correct={isCorrect} />
              <AnswerBlock label="Correct answer" values={correctIds} correct />
            </div>
          )}

          <div className="mb-3">
            <div className="text-xs uppercase tracking-wide text-faint font-normal mb-1.5">
              Explanation
            </div>
            <MarkdownRenderer
              content={evaluation.explanation || '_No explanation provided._'}
              className="prose prose-sm max-w-none text-read leading-relaxed"
            />
          </div>

          {evaluation.concepts_to_review?.length > 0 && (
            <div className="mb-3">
              <div className="text-xs uppercase tracking-wide text-faint font-normal mb-1.5">
                Concepts to review
              </div>
              <div className="flex flex-wrap gap-1.5">
                {evaluation.concepts_to_review.map((c) => (
                  <span key={c} className="text-xs text-mutedink bg-surface-subtle px-2 py-0.5 rounded">
                    {c.replaceAll('_', ' ')}
                  </span>
                ))}
              </div>
            </div>
          )}

          <ReportButton questionId={evaluation.question_id} />
        </div>
      )}
    </Card>
  );
}

// Every option with its text, as on the quiz page: correct ones green, a wrong
// pick red, the rest dimmed. The learner sees what they chose, not just the key.
function OptionsReview({ options, picked, correctIds }) {
  return (
    <div className="q-options mb-4">
      {picked.length === 0 && (
        <span className="text-xs text-faint italic">You skipped this question.</span>
      )}
      {options.map((option) => {
        const isPicked = picked.includes(option.option_id);
        const isRight = correctIds.includes(option.option_id);
        const state = isRight ? 'correct' : isPicked ? 'wrong' : 'muted';
        return (
          <div key={option.option_id} className="q-opt" data-state={state}>
            <span className="q-key">{option.option_id}</span>
            <MarkdownRenderer content={option.text} className="prose prose-sm max-w-none flex-1" />
            {(isPicked || isRight) && (
              <span className="text-xs font-medium flex-shrink-0 text-mutedink">
                {isPicked && isRight ? 'Your answer · correct'
                  : isPicked ? 'Your answer'
                  : 'Correct answer'}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

function AnswerBlock({ label, values = [], correct }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-faint font-normal mb-1.5">
        {label}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {values.length === 0 ? (
          <span className="text-xs text-faint italic">No answer</span>
        ) : (
          values.map((v, i) => (
            <span
              key={i}
              className={`px-2 py-0.5 rounded text-xs font-medium ${
                correct
                  ? 'bg-[var(--ok-soft)] text-emerald-700 border border-emerald-100'
                  : 'bg-surface-subtle text-mutedink border border-line'
              }`}
            >
              {v}
            </span>
          ))
        )}
      </div>
    </div>
  );
}

function ReportButton({ questionId }) {
  const [reported, setReported] = React.useState(false);
  const [showMenu, setShowMenu] = React.useState(false);

  const report = async (reason) => {
    try {
      await apiService.reportQuestion(questionId, reason);
      setReported(true);
      setShowMenu(false);
    } catch {
      // Silent fail — non-critical
    }
  };

  if (reported) {
    return <span className="text-xs text-faint">Reported — thanks.</span>;
  }

  return (
    <div className="relative">
      <button
        onClick={() => setShowMenu(!showMenu)}
        className="flex items-center gap-1 text-xs text-faint hover:text-read transition-colors"
      >
        <Flag size={11} /> Report issue
      </button>
      {showMenu && (
        <div className="absolute bottom-full left-0 mb-1 bg-surface border border-line rounded-lg shadow-md py-1 z-10 w-48">
          {[
            ['wrong_answer', 'Wrong answer'],
            ['unclear',      'Unclear question'],
            ['irrelevant',   'Irrelevant to topic'],
            ['too_easy',     'Too easy'],
            ['too_hard',     'Too hard'],
          ].map(([reason, label]) => (
            <button
              key={reason}
              onClick={() => report(reason)}
              className="w-full text-left px-3 py-1.5 text-sm text-read hover:bg-surface-muted"
            >
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
